import { useEffect, useMemo } from 'react';
import { useDrag } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import styled, { css } from 'styled-components';

import { CenterFlex, Divider, Flex, VFlex } from '@/components/base';
import Checkbox from '@/components/Checkbox/Checkbox';
import Tooltip from '@/components/Tooltip/TooltipV2';
import Typo, { SpanStyle } from '@/components/Typography/Typo';
import TicketLabel from '@/features/Commons/Ticket/TicketLabel';
import { radius } from '@/styles/sizes';
import { CategoryDic, CategoryIconDic } from '@/types/categoryConst';
import type { FeedbackListItemWithBlock } from '@/types/feedback';
import { sentimentMap } from '@/types/feedbackConst';
import {
  HighlightTextTokenType,
  TAXONOMY_DND_ITEM,
} from '@/types/taxonomyConst';
import textUtils from '@/utils/string';
import { DATE_FORMAT, getTimezoneDayjs } from '@/utils/time';

import type { TaxonomyDragContextT } from '../../context/TaxonomyDragContext';

type Props = {
  feedback: FeedbackListItemWithBlock;
  showBlocks?: boolean;
  selectedFeedback: SelectedFeedbackState;
  setSelectedFeedback: (params: SelectedFeedbackState) => void;

  // 블록 선택 관련
  selectedUnit?: ClassifiedFeedbackUnit | null;
  onSelect?: (unit: ClassifiedFeedbackUnit) => void;
  blockHighlight?: boolean;

  // for taxonomy drag
  dragOptions?: {
    dragContext?: TaxonomyDragContextT;
    selectedList?: ClassifiedFeedbackUnit[];
    currentTopicAlias?: string;
    inUnlinkedFeedback?: boolean;
  };
};

export type FeedbackCardSelectOption = {
  isSelected: (alias: string) => boolean;
};

export type FeedbackCardDragItem = {
  feedbacks: ClassifiedFeedbackUnit[];
  isMultiple: boolean;
};

const FeedbackCard = (props: Props) => {
  const { dragOptions } = props;
  const { dragContext } = dragOptions || {};
  const { showBlocks = true } = props;
  const selectedBlockList = props.selectedUnit?.block_id_list || [];

  const hasBlock = props.feedback.blocks && props.feedback.blocks.length > 0;

  const [{ isDragging }, drag, preview] = useDrag(
    () => ({
      type: TAXONOMY_DND_ITEM,
      item: () => {
        // 드래그 시작할 때 현재 카드가 선택된 상태라면 전체 선택 리스트를 전달
        if (dragOptions?.selectedList && dragOptions.selectedList.length > 0) {
          return {
            feedbacks: dragOptions.selectedList,
            isMultiple: true,
          };
        }
      },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    }),
    [dragOptions?.selectedList],
  );

  useEffect(() => {
    if (isDragging) {
      dragContext?.setIsDragging({
        alias: dragOptions?.currentTopicAlias || '',
        isTaxonomy: !dragOptions?.currentTopicAlias,
        forceRemove: !!dragOptions?.inUnlinkedFeedback,
      });
    } else {
      dragContext?.setIsDragging(null);
    }
  }, [
    isDragging,
    dragContext?.setIsDragging,
    dragOptions?.currentTopicAlias,
    dragOptions?.inUnlinkedFeedback,
  ]);

  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  }, [preview]);

  const isDragable = useMemo(() => {
    if (!dragOptions?.selectedList) return false;
    return dragOptions.selectedList.some(
      (f) => f.feedback_id === props.feedback.alias,
    );
  }, [dragOptions?.selectedList, props.feedback.alias]);

  return (
    <Wrapper
      g={12}
      a="center"
      selected={props.selectedFeedback?.feedbackAlias === props.feedback.alias}
      ref={isDragable ? drag : null}
      style={{
        transition: 'opacity 0.2s',
        opacity:
          dragOptions?.dragContext?.isDragging &&
          dragOptions?.selectedList?.some(
            (f) => f.feedback_id === props.feedback.alias,
          )
            ? 0.5
            : 1,
      }}
    >
      <Style>
        <Flex className="feedback-area">
          {props.onSelect && (
            <CenterFlex style={{ paddingLeft: 4 }}>
              <Tooltip
                tooltipNode={
                  hasBlock ? null : <Typo>No feedback blocks found</Typo>
                }
              >
                <div>
                  <Checkbox
                    disabled={!hasBlock}
                    checked={selectedBlockList.length > 0}
                    halfCheck={
                      selectedBlockList.length > 0
                        ? selectedBlockList.length <
                          (props.feedback.blocks?.length || 0)
                        : false
                    }
                    onChange={(status) => {
                      if (status) {
                        props.onSelect?.({
                          feedback_id: props.feedback.alias,
                          block_id_list:
                            props.feedback.blocks?.map(
                              (block) => block.alias,
                            ) || [],
                        });
                      } else {
                        props.onSelect?.({
                          feedback_id: props.feedback.alias,
                          block_id_list: [],
                        });
                      }
                    }}
                  />
                </div>
              </Tooltip>
            </CenterFlex>
          )}
          <VFlex
            f={1}
            g={8}
            className="title-area"
            onClick={() =>
              props.setSelectedFeedback({
                feedbackAlias: props.feedback.alias,
              })
            }
          >
            <Flex a="center" j="space-between">
              <Flex a="center" g={6}>
                <TicketLabel type="feedback">{props.feedback.tid}</TicketLabel>
                <Tooltip tooltipNode={props.feedback.company_info?.name}>
                  <Typo
                    type="label3"
                    weight="r"
                    className="text-gray-700"
                    ellipsis
                    style={{ maxWidth: 200 }}
                  >
                    {props.feedback.company_info?.name}
                  </Typo>
                </Tooltip>
                <Tooltip tooltipNode={props.feedback.customer_info?.name}>
                  <Typo
                    type="label3"
                    weight="r"
                    className="text-gray-600"
                    ellipsis
                    style={{ maxWidth: 200 }}
                  >
                    {props.feedback.customer_info?.name}
                  </Typo>
                </Tooltip>
              </Flex>
              <Typo>
                {getTimezoneDayjs(props.feedback.date).format(
                  DATE_FORMAT.readable,
                )}
              </Typo>
            </Flex>
            <VFlex className="info-area" g={8}>
              <Typo
                ellipsis
                type={hasBlock ? 'label3' : 'label2'}
                style={{ paddingLeft: 2 }}
              >
                {props.feedback.title}
              </Typo>
            </VFlex>
          </VFlex>
        </Flex>
        {showBlocks &&
          props.feedback.blocks &&
          props.feedback.blocks.length > 0 && (
            <>
              <Divider />
              <VFlex padding="8px 6px" g={4}>
                {props.feedback.blocks?.map((block, index) => (
                  <BlockItem
                    key={block.alias}
                    highlights={props.feedback.highlights}
                    selectedUnit={props.selectedUnit}
                    feedbackAlias={props.feedback.alias}
                    block={block}
                    selected={
                      props.selectedFeedback?.blockAlias === block.alias
                    }
                    onSelect={props.onSelect}
                    onClick={() =>
                      props.setSelectedFeedback({
                        feedbackAlias: props.feedback.alias,
                        blockAlias: block.alias,
                      })
                    }
                  />
                ))}
              </VFlex>
            </>
          )}
      </Style>
    </Wrapper>
  );
};

const Wrapper = styled(Flex)<{ selected: boolean; disabled?: boolean }>`
  ${({ selected }) =>
    selected &&
    css`
      background-color: ${(props) => props.theme.colors.gray200};
      border-color: ${(props) => props.theme.colors.primary100};
    `}
`;

const Style = styled(VFlex)`
  flex: 1;
  border-radius: ${radius.token.lg}px;
  border: 1px solid ${(props) => props.theme.colors.gray400};
  background-color: ${(props) => props.theme.colors.gray100};
  width: 100%;

  .feedback-area {
    padding: 4px;

    .title-area {
      cursor: pointer;
      padding: 8px;
      border-radius: ${radius.token.md}px;
      ${SpanStyle({
        type: 'label3',
        weight: 'r',
      })}
      color: ${(props) => props.theme.colors.gray600};
      transition: all 0.2s;
      &:hover {
        background-color: ${(props) => props.theme.colors.gray200};
      }
      .info-area {
        color: ${(props) => props.theme.colors.gray600};
      }
    }
  }
`;

type BlockItemProps = {
  feedbackAlias: string;
  block: FeedbackBlockT;
  disabled?: boolean;
  selected?: boolean;
  onClick: () => void;
  selectedUnit?: ClassifiedFeedbackUnit | null;
  onSelect?: (unit: ClassifiedFeedbackUnit) => void;
  highlights?: HighlightTextResult[];
};

const BlockItem = (props: BlockItemProps) => {
  const checked = !!props.selectedUnit?.block_id_list?.includes(
    props.block.alias,
  );

  const CategoryIcon = CategoryIconDic[props.block.category];

  const setiment = sentimentMap[props.block.sentiment || 2];

  const textDisplay = useMemo(() => {
    if (!props.highlights) return props.block.snippet;
    let hitArr: HighlightTextToken[] = [];
    for (const highlight of props.highlights) {
      hitArr.push(
        ...highlight.texts.filter(
          (text) => text.type === HighlightTextTokenType.HIT,
        ),
      );
    }
    const hitArrSet = hitArr.reduce((acc, value) => {
      acc.add(value.value);
      return acc;
    }, new Set<string>());

    const hitArrSetArr = [...hitArrSet];

    let result = props.block.snippet;

    const START_REPLACER = '#%%#';
    const END_REPLACER = '#%%%#';

    for (const hit of hitArrSetArr) {
      const matchedIndices = textUtils.getMatchedIndices(result, hit);
      if (matchedIndices) {
        const { startIndex, endIndex } = matchedIndices;
        const beforeMatch = result.slice(0, startIndex);
        const match = result.slice(startIndex, endIndex);
        const afterMatch = result.slice(endIndex);
        result = `${beforeMatch}${START_REPLACER}${match}${END_REPLACER}${afterMatch}`;
      }
    }

    result = result.replaceAll(START_REPLACER, '<strong>');
    result = result.replaceAll(END_REPLACER, '</strong>');

    return result;
  }, [props.block.snippet, props.highlights]);

  return (
    <BlockItemStyle
      g={2}
      a="center"
      padding="8px 8px 8px 2px "
      selected={props.selected}
      onClick={props.onClick}
      checked={checked}
    >
      <CenterFlex className="hover-item" style={{ minWidth: 8 }}>
        {props.onSelect && (
          <Checkbox
            disabled={props.disabled}
            checked={checked}
            onChange={(status) => {
              let newBlockList = props.selectedUnit?.block_id_list || [];
              if (status) {
                newBlockList.push(props.block.alias);
                const set = new Set(newBlockList);
                const result = [...set];
                props.onSelect?.({
                  feedback_id: props.feedbackAlias,
                  block_id_list: result,
                });
              } else {
                const result = newBlockList.remove(props.block.alias);
                props.onSelect?.({
                  feedback_id: props.feedbackAlias,
                  block_id_list: result,
                });
              }
            }}
          />
        )}
      </CenterFlex>
      <Flex g={8} f={1} j="space-between">
        <Tooltip tooltipNode={props.block.snippet}>
          <Typo
            type="body2"
            weight="r"
            multilineEllipsis={1}
            dangerouslySetInnerHTML={{ __html: textDisplay }}
          />
        </Tooltip>
        <Flex>
          <Flex g={12} a="center" style={{ fontSize: 14 }}>
            <Tooltip tooltipNode={setiment.value}>
              <CenterFlex>{setiment.icon}</CenterFlex>
            </Tooltip>
            <Tooltip tooltipNode={CategoryDic[props.block.category]}>
              <CategoryIcon />
            </Tooltip>
          </Flex>
        </Flex>
      </Flex>
    </BlockItemStyle>
  );
};

const BlockItemStyle = styled(Flex)<{ selected?: boolean; checked?: boolean }>`
  border: 1px solid transparent;
  border-radius: ${radius.token.lg}px;
  cursor: pointer;
  transition: all 0.2s;
  &:hover {
    background-color: ${(props) => props.theme.colors.gray200};
  }
  ${({ checked }) =>
    checked &&
    css`
      background-color: ${(props) => props.theme.colors.gray200};
    `}
  ${({ selected }) =>
    selected &&
    css`
      background-color: ${(props) => props.theme.colors.gray200};
      border-color: ${(props) => props.theme.colors.primary100};
    `}
  .hover-item {
    opacity: ${({ checked }) => (checked ? 1 : 0)};
    transition: all 0.2s;
  }
  &:hover {
    .hover-item {
      opacity: 1;
    }
  }
`;

export default FeedbackCard;
